import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ExternalLink, KeyRound, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
} from '@buzzbee/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { UpgradePrompt } from '../components/common';
import { StoreConnectionForm, SuggestedFlows } from '../components/industry';
import { CalendarSettings } from '../components/calendar';
import { OwnPhoneNumber } from '../components/telephony';
import { FaqEditor, HoursEditor, LANGUAGES, ServicesEditor, normalizeServices, type Faq, type Hours, type Service } from '../components/editors';
import { ApiError, del, errorMessage, get, patch, post, put } from '../lib/api';
import { Link, useRouter } from '../lib/router';
import { hasFeature, useRefreshMe, useWorkspace } from '../lib/session';

function useSave(fn: (v: any) => Promise<unknown>, label = 'Saved') {
  const qc = useQueryClient();
  const { success, error } = useToast();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      success(label);
      void qc.invalidateQueries();
    },
    onError: (e) => error('Could not save', errorMessage(e)),
  });
}

/** Shown in their own sections (components/telephony.tsx, components/calendar.tsx), not the integrations list. */
const OWN_ACCOUNT_KINDS = ['twilio_account', 'calendar'];

function Section({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-4">{children}</CardContent>
    </Card>
  );
}

export function SettingsPage() {
  const ws = useWorkspace();
  const { search } = useRouter();
  const q = useQuery({ queryKey: ['settings'], queryFn: () => get('/api/settings') });
  const isAdmin = ws.role !== 'staff';
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const s = q.data;
  // ?tab=phone: the calendar sign-in returns here.
  const tab = ['business', 'services', 'kb', 'hours', 'locations', 'phone'].includes(search.get('tab') ?? '') ? search.get('tab')! : 'business';
  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description={isAdmin ? 'What your receptionist knows and how it behaves.' : 'Only owners and admins can change settings.'} actions={<Button asChild variant="outline"><Link to="/settings/billing">Plan and billing</Link></Button>} />
      <Tabs defaultValue={tab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="business">Business and voice</TabsTrigger>
          <TabsTrigger value="services">Services</TabsTrigger>
          <TabsTrigger value="kb">Knowledge base</TabsTrigger>
          <TabsTrigger value="hours">Hours and staff</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="phone">Phone, calendar and integrations</TabsTrigger>
        </TabsList>
        <TabsContent value="business" className="space-y-6">
          <BusinessSection s={s} />
          <OnlineBookingSection s={s} />
          <VoiceSection s={s} />
        </TabsContent>
        <TabsContent value="services">
          <ServicesSection initial={s.services} />
        </TabsContent>
        <TabsContent value="kb">
          <FaqSection initial={s.faq} />
        </TabsContent>
        <TabsContent value="hours" className="space-y-6">
          <HoursSection s={s} />
          <StaffSection s={s} />
        </TabsContent>
        <TabsContent value="locations">
          <LocationsSection s={s} />
        </TabsContent>
        <TabsContent value="phone">
          <PhoneSection s={s} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BusinessSection({ s }: { s: any }) {
  const [b, setB] = useState({ name: s.business.name, vertical: s.business.vertical, phone: s.business.phone ?? '' });
  const save = useSave(() => patch('/api/settings/business', { ...b, phone: b.phone || null }));
  return (
    <Section title="Business" description="Changing the template pack changes the default services, FAQs and safety rules (your own edits are kept).">
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="b-name">Name</Label>
          <Input id="b-name" value={b.name} onChange={(e) => setB({ ...b, name: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="b-pack">Template pack</Label>
          <Select id="b-pack" value={b.vertical} onChange={(e) => setB({ ...b, vertical: e.target.value })} options={s.packs.map((p: any) => ({ value: p.id, label: p.name }))} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="b-phone">Business phone</Label>
          <Input id="b-phone" value={b.phone} onChange={(e) => setB({ ...b, phone: e.target.value })} />
        </div>
      </div>
      <Button onClick={() => save.mutate(null)} loading={save.isPending}>
        Save
      </Button>
      {s.suggestedFlows?.length ? (
        <div className="space-y-1.5 pt-2">
          <p className="text-sm font-medium">Suggested flows for {s.business.verticalName}</p>
          <SuggestedFlows flows={s.suggestedFlows} />
        </div>
      ) : null}
    </Section>
  );
}

/** The public booking page link (/book/:slug) and the missed-call text-back that sends it. */
function OnlineBookingSection({ s }: { s: any }) {
  const ws = useWorkspace();
  const canEdit = ws.role !== 'staff';
  const toast = useToast();
  const [message, setMessage] = useState<string>(s.textback.message ?? '');
  const setKey = useSave((v: { key: string; value: unknown }) => put('/api/settings/config', v));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(s.bookingPage.url);
      toast.success('Booking link copied');
    } catch {
      toast.error('Could not copy', 'Select the link and copy it.');
    }
  };
  return (
    <Section title="Online booking" description="Customers pick a service, a time and (optionally) who they want, and book themselves. Bookings follow the same rules as your receptionist.">
      {s.bookingPage.open ? (
        <div className="space-y-1.5">
          <Label htmlFor="bk-link">Your booking link</Label>
          <div className="flex gap-2">
            <Input id="bk-link" readOnly value={s.bookingPage.url} onFocus={(e) => e.currentTarget.select()} className="figures" />
            <Button variant="outline" onClick={copy}>
              <Copy /> Copy
            </Button>
            <Button asChild variant="ghost" size="icon" aria-label="Open booking page">
              <a href={s.bookingPage.url} target="_blank" rel="noreferrer">
                <ExternalLink />
              </a>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Add this as your booking link on Google Business Profile, Instagram and your website.</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{s.bookingPage.enabled ? 'Online booking is not available for this business type or plan.' : 'Your booking page is turned off.'}</p>
      )}
      {canEdit ? (
        <div className="space-y-4">
          <Checkbox checked={s.bookingPage.enabled} onCheckedChange={(v) => setKey.mutate({ key: 'booking.page_enabled', value: v === true })} label="Take bookings online" />
          {s.textback.available ? (
            <>
              <Checkbox checked={s.textback.enabled} onCheckedChange={(v) => setKey.mutate({ key: 'textback.enabled', value: v === true })} label="Text callers we miss (a hang-up or an unanswered transfer) with your booking link" />
              {s.textback.enabled ? (
                <div className="space-y-1.5">
                  <Label htmlFor="tb-msg">Missed-call text (optional)</Label>
                  <Textarea id="tb-msg" rows={2} maxLength={320} value={message} placeholder={`${s.business.name}: sorry we missed your call. Book online: ${s.bookingPage.url} or reply to this text.`} onChange={(e) => setMessage(e.target.value)} />
                  <p className="text-xs text-muted-foreground">Leave empty for the default. "Reply STOP to opt out" is added automatically.</p>
                  <Button size="sm" variant="outline" loading={setKey.isPending} onClick={() => setKey.mutate({ key: 'textback.message', value: message.trim() || null })}>
                    Save text
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </Section>
  );
}

function VoiceSection({ s }: { s: any }) {
  const [v, setV] = useState({ disclosure: s.voice.disclosure, greeting: s.voice.greeting ?? '', defaultLanguage: s.voice.defaultLanguage });
  const save = useSave(() => put('/api/settings/voice', { ...v, greeting: v.greeting || null }));
  return (
    <Section title="Voice" description={`Speaks: ${s.voice.activeLanguages.map((c: string) => LANGUAGES.find((l) => l.code === c)?.name).join(', ')}.`}>
      <div className="space-y-1.5">
        <Label htmlFor="v-disc">AI and recording disclosure (required)</Label>
        <Textarea id="v-disc" rows={2} value={v.disclosure} onChange={(e) => setV({ ...v, disclosure: e.target.value })} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="v-greet">Greeting</Label>
          <Input id="v-greet" value={v.greeting} onChange={(e) => setV({ ...v, greeting: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="v-lang">Main language</Label>
          <Select id="v-lang" value={v.defaultLanguage} onChange={(e) => setV({ ...v, defaultLanguage: e.target.value })} options={LANGUAGES.map((l) => ({ value: l.code, label: l.name }))} />
        </div>
      </div>
      <Button onClick={() => save.mutate(null)} loading={save.isPending}>
        Save
      </Button>
    </Section>
  );
}

function ServicesSection({ initial }: { initial: Service[] }) {
  const ws = useWorkspace();
  const [list, setList] = useState<Service[]>(initial);
  const save = useSave(() => put('/api/settings/services', { services: normalizeServices(list) }));
  return (
    <Section title="Services" description="The receptionist only offers and books these.">
      <ServicesEditor value={list} onChange={setList} extras={{ deposits: hasFeature(ws, 'no_show_rescue'), rebooking: hasFeature(ws, 'review_requests') }} />
      <Button onClick={() => save.mutate(null)} loading={save.isPending}>
        Save services
      </Button>
    </Section>
  );
}

function FaqSection({ initial }: { initial: Faq[] }) {
  const [list, setList] = useState<Faq[]>(initial);
  const save = useSave(() => put('/api/settings/faq', { faq: list.filter((f) => f.question.trim() && f.answer.trim()) }));
  return (
    <Section title="Knowledge base" description="Answers to common questions. The receptionist never invents prices or policies.">
      <FaqEditor value={list} onChange={setList} />
      <Button onClick={() => save.mutate(null)} loading={save.isPending}>
        Save answers
      </Button>
    </Section>
  );
}

function HoursSection({ s }: { s: any }) {
  const primary = s.locations.find((l: any) => l.id === s.primaryLocationId);
  const [hours, setHours] = useState<Hours>(primary?.businessHours ?? {});
  const save = useSave(() => put('/api/settings/hours', { businessHours: hours }));
  return (
    <Section title="Opening hours" description={`Times are in ${primary?.timezone ?? 'your time zone'}. Calls outside these hours are still answered 24/7.`}>
      <HoursEditor value={hours} onChange={setHours} />
      <Button onClick={() => save.mutate(null)} loading={save.isPending}>
        Save hours
      </Button>
    </Section>
  );
}

function StaffSection({ s }: { s: any }) {
  const [name, setName] = useState('');
  const add = useSave(() => post('/api/staff', { name, services: [] }), 'Staff added');
  const remove = useSave((id: string) => del(`/api/staff/${id}`), 'Removed');
  const toggle = useSave((v: { id: string; services: string[] }) => patch(`/api/staff/${v.id}`, { services: v.services }));
  return (
    <Section title="Staff" description="With staff, a time is offered when someone who does that service is free. Leave services empty for 'everything'.">
      {s.staff.map((m: any) => (
        <div key={m.id} className="space-y-2 rounded-lg bg-muted/40 p-3">
          <div className="flex items-center gap-2">
            <span className="flex-1 font-medium">{m.name}</span>
            <Button variant="ghost" size="icon" aria-label={`Remove ${m.name}`} onClick={() => remove.mutate(m.id)}>
              <Trash2 />
            </Button>
          </div>
          <div className="flex flex-wrap gap-3">
            {s.services.map((svc: any) => (
              <label key={svc.id} className="flex items-center gap-1.5 text-sm">
                <input type="checkbox" checked={m.services.includes(svc.id)} onChange={(e) => toggle.mutate({ id: m.id, services: e.target.checked ? [...m.services, svc.id] : m.services.filter((x: string) => x !== svc.id) })} />
                {svc.name}
              </label>
            ))}
          </div>
        </div>
      ))}
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) add.mutate(null);
          setName('');
        }}
      >
        <Input aria-label="New staff name" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" variant="outline">
          <Plus /> Add
        </Button>
      </form>
    </Section>
  );
}

function LocationsSection({ s }: { s: any }) {
  const ws = useWorkspace();
  const [name, setName] = useState('');
  const qc = useQueryClient();
  const add = useMutation({ mutationFn: () => post('/api/locations', { name }), onSuccess: () => qc.invalidateQueries() });
  const upgrade = add.error instanceof ApiError ? add.error.upgrade : null;
  return (
    <Section title="Locations" description={`Your plan includes ${ws.entitlements.limits.locations ?? 'unlimited'} location${ws.entitlements.limits.locations === 1 ? '' : 's'}.`}>
      {s.locations.map((l: any) => (
        <div key={l.id} className="flex flex-wrap items-center gap-2 text-sm">
          <strong>{l.name}</strong> {l.isPrimary ? <Badge>Primary</Badge> : null}
          <span className="text-muted-foreground">{l.address}</span>
          <span className="figures ml-auto text-muted-foreground">{l.agentPhoneNumber ?? 'No number yet'}</span>
        </div>
      ))}
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <Input aria-label="New location name" placeholder="New location name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Button type="submit" variant="outline" loading={add.isPending}>
          <Plus /> Add location
        </Button>
      </form>
      {upgrade ? <UpgradePrompt upgrade={upgrade} compact /> : add.error ? <Alert variant="destructive">{errorMessage(add.error)}</Alert> : null}
    </Section>
  );
}

function PhoneSection({ s }: { s: any }) {
  const ws = useWorkspace();
  const [key, setKey] = useState<string | null>(null);
  const rotate = useMutation({ mutationFn: () => post<{ agentKey: string }>('/api/settings/agent-key/rotate'), onSuccess: (r) => setKey(r.agentKey) });
  const toast = useToast();
  // Pushes the current prompt, languages, locations, team and tools to ElevenLabs (it keeps a copy).
  const resync = useMutation({
    mutationFn: () => post<{ ok: boolean }>('/api/settings/agent/sync'),
    onSuccess: () => toast.success('Re-synced with ElevenLabs', 'Calls now use your latest settings.'),
    onError: (e) => toast.error('Could not re-sync with ElevenLabs', errorMessage(e)),
  });
  const elevenLabs = s.integrations.some((i: any) => i.kind === 'telephony' && i.provider === 'elevenlabs');
  const primary = s.locations.find((l: any) => l.id === s.primaryLocationId);
  return (
    <div className="space-y-6">
      <Section title="Phone">
        <p className="text-sm">
          Receptionist number: <strong className="figures">{primary?.agentPhoneNumber ?? 'not set'}</strong>
          {primary?.forwardingFromNumber ? (
            <>
              {' '}
              · forwarded from <span className="figures">{primary.forwardingFromNumber}</span> ({primary.forwardingMode === 'all' ? 'every call' : primary.forwardingMode === 'no_answer' ? 'calls you miss' : 'forwarding type not set'})
            </>
          ) : null}
        </p>
        <Button asChild variant="outline" size="sm">
          <Link to="/onboarding">Change phone setup</Link>
        </Button>
      </Section>
      {s.books ? (
        <Section title="Calendar" description="Where your receptionist checks for free times and adds bookings.">
          <CalendarSettings canEdit={ws.role !== 'staff'} />
        </Section>
      ) : null}
      <Section title="Your own phone number">
        <OwnPhoneNumber canEdit={ws.role !== 'staff'} />
      </Section>
      {s.ordersEnabled ? (
        <Section title="Online store" description="Lets your assistant look up real order status when customers ask where their order is.">
          <StoreConnectionForm store={s.store} />
        </Section>
      ) : null}
      <Section title="Integrations">
        {s.integrations.filter((i: any) => !OWN_ACCOUNT_KINDS.includes(i.kind)).map((i: any) => (
          <p key={i.id} className="text-sm">
            <strong className="capitalize">{i.kind}</strong>: {i.provider} <Badge variant={i.status === 'connected' ? 'success' : 'default'}>{i.status}</Badge>
          </p>
        ))}
        {elevenLabs && ws.role !== 'staff' ? (
          <div className="space-y-1.5">
            <Button variant="outline" size="sm" onClick={() => resync.mutate()} loading={resync.isPending}>
              <RefreshCw /> Re-sync with ElevenLabs
            </Button>
            <p className="text-xs text-muted-foreground">Sends your latest services, hours, locations, team and languages to the voice agent. Do this after changing settings.</p>
          </div>
        ) : null}
      </Section>
      <Section title="Voice provider tool key" description={<>Your voice provider calls <code className="figures">{s.toolsUrl}/&lt;tool&gt;</code> with this key as a Bearer token. Current key starts with <code>bzk_{s.agentKeyPrefix}</code>.</>}>
        {key ? <Alert variant="warning" title="Copy it now; it is shown once."><code className="figures break-all">{key}</code></Alert> : null}
        <Button variant="outline" onClick={() => rotate.mutate()} loading={rotate.isPending}>
          <KeyRound /> Rotate key
        </Button>
      </Section>
    </div>
  );
}

export function BillingPage() {
  const ws = useWorkspace();
  const refresh = useRefreshMe();
  const { search } = useRouter();
  const { success, error } = useToast();
  const q = useQuery({ queryKey: ['billing'], queryFn: () => get('/api/billing') });
  const usage = useQuery({ queryKey: ['usage'], queryFn: () => get('/api/usage') });
  const [cap, setCap] = useState('');
  useEffect(() => {
    if (usage.data) setCap(String(usage.data.capCents / 100));
  }, [usage.data]);
  const checkout = useMutation({
    mutationFn: (plan: string) => post<{ url: string | null }>('/api/billing/checkout', { plan }),
    onSuccess: (r) => {
      if (r.url) window.location.href = r.url;
      else {
        success('Plan updated');
        void refresh();
      }
    },
    onError: (e) => error('Could not change plan', errorMessage(e)),
  });
  const portal = useMutation({ mutationFn: () => post<{ url: string }>('/api/billing/portal'), onSuccess: (r) => (window.location.href = r.url), onError: (e) => error('Portal unavailable', errorMessage(e)) });
  const saveCap = useSave(() => put('/api/usage/cap', { capCents: Math.round(Number(cap) * 100) }), 'Cap saved');
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} />;
  const b = q.data;
  const hive = b.managedBy === 'hive';
  const canManage = ws.role !== 'staff';
  return (
    <div className="space-y-6">
      <PageHeader title="Plan and billing" description={`You're on ${b.entitlements.planName} (${b.entitlements.state.replace('_', ' ')}).`} />
      {search.get('checkout') === 'success' ? <Alert variant="success" title="Thanks! Your subscription is active." /> : null}
      {hive ? (
        <Alert variant="info" title="Billing is managed in WRKRBZ Hive">
          <a className="inline-flex items-center gap-1 font-medium underline" href={b.manageUrl ?? '#'}>
            Manage in Hive <ExternalLink className="size-3" />
          </a>
        </Alert>
      ) : !b.stripeConfigured ? (
        <Alert variant="info" title="Development billing">Stripe is not configured, so plan changes apply immediately without payment.</Alert>
      ) : null}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {b.plans.map((p: any) => {
          const current = p.id === b.entitlements.plan;
          return (
            <Card key={p.id} data-brand={p.product} className={current ? 'ring-2 ring-primary' : ''}>
              <CardContent className="flex h-full flex-col gap-3 p-6">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{p.productName}</p>
                <h3 className="text-lg font-semibold">{p.name}</h3>
                <p>
                  <span className="figures text-2xl font-semibold">{p.price}</span>
                  <span className="text-muted-foreground"> /mo</span>
                </p>
                <ul className="flex-1 space-y-1 text-sm text-muted-foreground">
                  {p.highlights.map((h: string) => (
                    <li key={h}>· {h}</li>
                  ))}
                </ul>
                {current ? (
                  <Badge variant="success" className="self-start">
                    Current plan
                  </Badge>
                ) : hive ? (
                  <Button asChild variant="outline">
                    <a href={b.manageUrl ?? '#'}>Change in Hive</a>
                  </Button>
                ) : (
                  <Button variant={p.product === 'receptionistbee' && b.entitlements.product === 'buzzbee' ? 'primary' : 'outline'} disabled={!canManage} loading={checkout.isPending && checkout.variables === p.id} onClick={() => checkout.mutate(p.id)}>
                    {p.priceCents > (b.plans.find((x: any) => x.id === b.entitlements.plan)?.priceCents ?? 0) ? 'Upgrade' : 'Switch'}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
      {!hive && b.hasSubscription && b.stripeConfigured ? (
        <Button variant="outline" onClick={() => portal.mutate()}>
          Manage payment method and invoices
        </Button>
      ) : null}
      {usage.data ? (
        <Section title="Voice minutes this month" description={`Extra minutes cost ${b.overageRate} each, up to your cap. At the cap the receptionist pauses and owners are emailed.`}>
          <p className="figures text-sm">
            {usage.data.minutesUsed} used of {usage.data.includedMinutes ?? 'unlimited'} included · {usage.data.overageMinutes} extra (${(usage.data.overageCents / 100).toFixed(2)})
          </p>
          <div className="flex items-end gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="cap">Monthly overage cap (CAD)</Label>
              <Input id="cap" type="number" min={0} step={5} className="w-40" value={cap} onChange={(e) => setCap(e.target.value)} />
            </div>
            <Button variant="outline" disabled={!canManage} onClick={() => saveCap.mutate(null)} loading={saveCap.isPending}>
              Save cap
            </Button>
          </div>
        </Section>
      ) : null}
    </div>
  );
}
